import * as React from "react";
import { Navigate, Outlet, RouterProvider, createBrowserRouter } from "react-router-dom";
import { IsReactRouterPresentContext } from "spry-react/App/routerContext";
import { ErrorBoundary } from "./Utils/ErrorHandling/errorBoundary";
import { PortalPageNotFound } from "./Utils/ErrorHandling/portalPageNotFound";
import { RouterErrorBoundary } from "./Utils/ErrorHandling/routerErrorBoundary";
import { ErrorLogger } from "./Utils/ErrorHandling/errorLogger";
import { PortalLoadingPage } from "./Utils/portalLoadingPage";
import { ReactRouterLoadingBanner } from "./Utils/reactRouterLoadingBanner";
import { ClaimRoutes, ClaimsUrls } from "./Claims/claimsRoutes";
import { paymentHistoryRoutes } from "./PaymentHistory/paymentHistoryRoutes";
import { accountSettingsRoutes } from "./AccountSettings/accountSettingsRoutes";
import { CrossSiteRequestForgeryTokenInfo } from "spry-react/utils/fetchExtensions";
import { configureLoaderApiClient } from "./Shared/configureFetchers";
import { AppWithContext } from "./Shared/appWithContext";
import { PortalLayout } from "./Api/GeneratedDTOs/ViewModels/Shared/PortalLayout";
import { CloseDialogsOnRouterNavigate } from "spry-react/Dialog/useCloseDialogsOnRouterNavigate";
import { loginRoutes, LoginUrls } from "./Login/loginRoutes";
import { registrationRoutes } from "./Registration/registrationRoutes";
import { manageUsersRoutes } from "./ManageUsers/manageUsersRoutes";

export interface Props {
  errorLogger: ErrorLogger;
  layout: PortalLayout;
  getCsrfInfo: () => CrossSiteRequestForgeryTokenInfo;
}

/**
 * Renders the React Router for the application including all client side routes.
 * This component renders the ErrorBoundary, the spry-react App component, and
 * builds the PortalContext object so parent components do not need to do this.
 */
export function AppRouter(props: Props) {
  const loaderApiClient = configureLoaderApiClient(props.getCsrfInfo, props.layout.jsonCommandApiUrls);

  // NOTE: traditionally our page components took the IPortalAppContext as a prop,
  // but we can not do that in client side routed pages because the router does not know about the context yet.
  // So instead the AppWithContext component loads the context into React context, and the client side routed pages
  // use the usePortalAppContext() hook to get it.
  const routes = [
    ...loginRoutes(loaderApiClient),
    accountSettingsRoutes(loaderApiClient),
    ClaimRoutes(loaderApiClient),
    paymentHistoryRoutes(loaderApiClient),
    registrationRoutes(loaderApiClient),
    manageUsersRoutes(loaderApiClient),
  ];

  routes.push({
    path: '/',
    element: <Navigate to={props.layout.loggedInUser ? ClaimsUrls.list() : LoginUrls.login()} replace />
  });
  
  routes.push({
    path: "*",
    element: <PortalPageNotFound />,
  });

  // NOTE: it is very important that the <App> is rendered inside of the router.
  // Otherwise, dialogs would not be inside of the router and would be unable to use
  // router hooks, like useBlocker.
  const router = createBrowserRouter([
    {
      element: (
        <ErrorBoundary errorLogger={props.errorLogger}>
          <>
            <AppWithContext
              layout={props.layout}
              getCsrfInfo={props.getCsrfInfo}
              isClientSidePage={true}
              renderChildren={(context) => {
                return (
                  <>
                    <Outlet />
                    <CloseDialogsOnRouterNavigate />
                  </>
                );
              }}
            />
            <ReactRouterLoadingBanner />
          </>
        </ErrorBoundary>
      ),
      errorElement: (
        <AppWithContext
          layout={props.layout}
          getCsrfInfo={props.getCsrfInfo}
          isClientSidePage={true}
          renderChildren={(context) => {
            return <RouterErrorBoundary errorLogger={props.errorLogger} />;
          }}
        />
      ),
      children: routes,
    },
  ]);

  return (
    <IsReactRouterPresentContext.Provider value={true}>
      <RouterProvider
        router={router}
        fallbackElement={
          <AppWithContext
            layout={props.layout}
            getCsrfInfo={props.getCsrfInfo}
            isClientSidePage={true}
            renderChildren={(context) => {
              return <PortalLoadingPage context={context} />;
            }}
          />
        }
      />
    </IsReactRouterPresentContext.Provider>
  );
}
